import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { BenchmarkCollectionView, CollectionScoringStatus, ImageType } from '../../generated';
import { listBenchmarkCollectionsV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdCollectionsGet } from '../../generated';
import { extractTotalCount } from '../../utils';

/*
 * GET /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/collections: the collections scored against a
 * benchmark, a page at a time, with the total in `Vizit-Total-Count`. `toBenchmarkCollection` is the one place the
 * generated `BenchmarkCollectionView` is mapped. Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

/** A collection's latest scoring against a benchmark, mapped for the UI. */
export interface BenchmarkCollectionScoring {
  id: string;
  productCategoryId: string;
  productCategoryName: string;
  /** Pending while the collection is being rescored, e.g. after images were added to the benchmark. */
  status: CollectionScoringStatus;
  requestedAt: string;
  readyAt: string | null;
  lastSelectedAt: string | null;
}

/** A collection scored against a benchmark, mapped for the UI. */
export interface BenchmarkCollection {
  id: string;
  name: string;
  imageType: ImageType;
  /** The collection's own category; `null` for a collection without one. */
  productCategoryId: string | null;
  /** The collection's latest scoring against this benchmark. */
  lastScoring: BenchmarkCollectionScoring;
}

export function toBenchmarkCollectionScoring(
  response: BenchmarkCollectionView['last_scoring'],
): BenchmarkCollectionScoring {
  return {
    id: response.id,
    productCategoryId: response.product_category_id,
    productCategoryName: response.product_category_name,
    status: response.status,
    requestedAt: response.requested_at,
    readyAt: response.ready_at,
    lastSelectedAt: response.last_selected_at,
  };
}

export function toBenchmarkCollection(response: BenchmarkCollectionView): BenchmarkCollection {
  return {
    id: response.id,
    name: response.name,
    imageType: response.image_type,
    productCategoryId: response.product_category_id,
    lastScoring: toBenchmarkCollectionScoring(response.last_scoring),
  };
}

export interface ListBenchmarkCollectionsQueryVariables {
  workspace_id: string;
  benchmark_id: string;
  /** Collections per page; the API defaults to 10. */
  limit?: number;
  offset?: number;
}

export interface ListBenchmarkCollectionsQuery {
  collections: BenchmarkCollection[];
  /** Every collection scored against the benchmark, from the `Vizit-Total-Count` header. */
  totalCount: number;
}

async function listBenchmarkCollections(variables: ListBenchmarkCollectionsQueryVariables) {
  const { workspace_id, benchmark_id, ...query } = variables;
  const { data, response } = await listBenchmarkCollectionsV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdCollectionsGet({
    path: { workspace_id, benchmark_id },
    query,
    throwOnError: true,
  });
  const collections = (data ?? []).map(toBenchmarkCollection);

  return { collections, totalCount: extractTotalCount(response, collections.length) };
}

export const useListBenchmarkCollectionsQuery = <TData = ListBenchmarkCollectionsQuery, TError = unknown>(
  variables: ListBenchmarkCollectionsQueryVariables,
  options?: Omit<UseQueryOptions<ListBenchmarkCollectionsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ListBenchmarkCollectionsQuery, TError, TData>({
    queryKey: useListBenchmarkCollectionsQuery.getKey(variables),
    queryFn: () => listBenchmarkCollections(variables),
    ...options,
  });
};

useListBenchmarkCollectionsQuery.getKey = (variables: ListBenchmarkCollectionsQueryVariables) => [
  'ListBenchmarkCollections',
  variables,
];

useListBenchmarkCollectionsQuery.fetcher = (variables: ListBenchmarkCollectionsQueryVariables) => () =>
  listBenchmarkCollections(variables);
